// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline'
type ButtonSize    = 'sm' | 'md' | 'lg'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:  ButtonVariant
  size?:     ButtonSize
  loading?:  boolean
  icon?:     React.ReactNode
  iconRight?: React.ReactNode
  fullWidth?: boolean
}

const variants: Record<ButtonVariant, string> = {
  primary:
    'bg-moss text-white hover:bg-moss-light active:bg-sage-900 ' +
    'shadow-soft focus-visible:ring-sage-600',
  secondary:
    'bg-sand-200 text-ink hover:bg-sand-300 active:bg-sand-300 ' +
    'focus-visible:ring-sage-400',
  outline:
    'border border-sage-200 bg-transparent text-ink-2 hover:bg-sage-50 ' +
    'active:bg-sage-100 focus-visible:ring-sage-400',
  ghost:
    'bg-transparent text-ink-2 hover:bg-sage-50 active:bg-sage-100 ' +
    'focus-visible:ring-sage-400',
  danger:
    'bg-status-alertBg border border-status-alertBorder text-status-alertText ' +
    'hover:bg-red-100 active:bg-red-200 focus-visible:ring-red-400',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9  px-3.5 text-sm  gap-1.5',
  md: 'h-11 px-5   text-sm  gap-2',
  lg: 'h-13 px-6   text-base gap-2',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant   = 'primary',
      size      = 'md',
      loading   = false,
      icon,
      iconRight,
      fullWidth = false,
      className,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          // Base
          'inline-flex items-center justify-center rounded-xl font-medium',
          'transition-all duration-150 ease-soft active:scale-[0.98]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'select-none cursor-pointer',
          // Disabled state
          'disabled:opacity-50 disabled:pointer-events-none',
          // Variant
          variants[variant],
          // Size
          sizes[size],
          // Full width
          fullWidth && 'w-full',
          className,
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          icon && <span className="flex-shrink-0">{icon}</span>
        )}
        {children && <span>{children}</span>}
        {!loading && iconRight && (
          <span className="flex-shrink-0">{iconRight}</span>
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'
